import { supportServiceConnectClient } from "api";

import { SortDirection } from "@safetyculture/s12-apis-connect-web/s12/common/sorting_pb";
import {
  CreatePublisherRequest,
  DeleteProductRequest,
  ExportProductsRequest,
  GetPublisherRequest,
  PublishProductRequest,
  SearchProductsRequest,
  UnpublishProductRequest,
  UpdatePublisherRequest,
} from "@safetyculture/s12-apis-connect-web/s12/contentlibrary/admin/v1/request_pb";
import {
  CreatePublisherResponse,
  DeleteProductResponse,
  SearchProductsResponse,
  UnpublishProductResponse,
  UpdatePublisherResponse,
} from "@safetyculture/s12-apis-connect-web/s12/contentlibrary/admin/v1/response_pb";
import {
  BulkImportProductsRequest,
  DownloadFileTemplateRequest,
} from "@safetyculture/s12-apis-connect-web/s12/contentlibrary/migrations/admin/v1/migrations_pb";
import { GetProductPreviewProxyRequest } from "@safetyculture/s12-apis-connect-web/s12/contentlibrary/proxy/v1/request_pb";
import { GetProductPreviewProxyResponse } from "@safetyculture/s12-apis-connect-web/s12/contentlibrary/proxy/v1/response_pb";
import { PublisherType } from "@safetyculture/s12-apis-connect-web/s12/contentlibrary/v1/publisher_pb";
import {
  Filter,
  Pagination,
  Sort,
  Sort_Field,
} from "@safetyculture/s12-apis-connect-web/s12/contentlibrary/v1/query_pb";

import { RegionCode } from "../../types/region";
import { SC_REFERENCE_HEADER_KEY } from "api/constants";
import { callOptionsHeaders } from "api/helpers";
import { SortOption } from "components/search/content-library-search";
import { getScUserEmail } from "helpers/get-sc-user-email";

type unpublishProductParams = {
  productId: string;
};

type createPublisherParams = {
  orgId: string;
  title: string;
  slug: string;
  publisherType: PublisherType;
  enabled: boolean;
};

type updatePublisherParams = {
  orgId: string;
  publisherType: PublisherType;
  enabled: boolean;
  title: string;
  slug: string;
};

type FilterParams = {
  productType?: number;
  productState?: ProductStateSearch;
  title?: string;
  sourceContentId?: string;
  orgId?: string;
  slug?: string;
  publisherName?: string;
};

type SearchContentLibraryParams = {
  filterParams: FilterParams;
  sortOption: SortOption;
  paginationOption: PaginationOption;
};

export type PaginationOption = {
  pageSize: number;
  offset: number;
};

export type ProductStateSearch = "all" | "published" | "unpublished";

export const unpublishProduct = async ({
  productId,
}: unpublishProductParams): Promise<UnpublishProductResponse> => {
  const req = new UnpublishProductRequest({
    productId: productId,
  });

  const headers = {
    [SC_REFERENCE_HEADER_KEY]: productId,
  };

  return supportServiceConnectClient.unpublishProduct(req, { headers });
};

export const deleteProduct = async ({
  productId,
}: {
  productId: string;
}): Promise<DeleteProductResponse> => {
  const req = new DeleteProductRequest({
    productId,
  });

  const headers = {
    [SC_REFERENCE_HEADER_KEY]: productId,
  };

  return supportServiceConnectClient.deleteProduct(req, { headers });
};

export const publishProduct = async ({
  productId,
}: unpublishProductParams): Promise<UnpublishProductResponse> => {
  const req = new PublishProductRequest({
    productId,
  });

  const headers = {
    [SC_REFERENCE_HEADER_KEY]: productId,
  };
  return supportServiceConnectClient.publishProduct(req, { headers });
};

export const createPublisher = async (
  params: createPublisherParams,
): Promise<CreatePublisherResponse> => {
  const req = new CreatePublisherRequest(params);

  return supportServiceConnectClient.createPublisher(req);
};

export const updatePublisher = async (
  params: updatePublisherParams,
): Promise<UpdatePublisherResponse> => {
  const req = new UpdatePublisherRequest({
    orgId: params.orgId,
    publisherType: params.publisherType,
    enabled: params.enabled,
    name: params.title,
    slug: params.slug,
  });

  return supportServiceConnectClient.updatePublisher(req);
};

export const getPublisher = async (scOrgId: string) => {
  const req = new GetPublisherRequest({ id: scOrgId });
  return supportServiceConnectClient.getPublisher(req);
};

export type GetProductPreviewParams = {
  productId: string;
  publisherOrgId: string;
};

export const getProductPreview = async ({
  productId,
  publisherOrgId,
}: GetProductPreviewParams): Promise<GetProductPreviewProxyResponse> => {
  const req = new GetProductPreviewProxyRequest({
    productId,
    orgId: publisherOrgId,
  });

  const headers = {
    [SC_REFERENCE_HEADER_KEY]: productId,
  };

  return supportServiceConnectClient.getProductPreviewProxy(req, { headers });
};

function convertFiltersToGrpc(filterParams: FilterParams) {
  const filters: Filter[] = [];

  if (filterParams.title) {
    filters.push(
      new Filter({ field: { case: "title", value: filterParams.title } }),
    );
  }
  if (filterParams.sourceContentId) {
    filters.push(
      new Filter({
        field: { case: "productId", value: filterParams.sourceContentId },
      }),
    );
  }
  if (filterParams.orgId) {
    filters.push(
      new Filter({
        field: { case: "productOrgId", value: filterParams.orgId },
      }),
    );
  }
  if (filterParams.slug) {
    filters.push(
      new Filter({ field: { case: "productSlug", value: filterParams.slug } }),
    );
  }
  if (filterParams.publisherName) {
    filters.push(
      new Filter({
        field: { case: "publisherName", value: filterParams.publisherName },
      }),
    );
  }
  if (filterParams.productType) {
    filters.push(
      new Filter({
        field: { case: "productType", value: filterParams.productType },
      }),
    );
  }

  if (
    filterParams.productState !== undefined &&
    filterParams.productState !== "all"
  ) {
    filters.push(
      new Filter({
        field: {
          case: "publishedProducts",
          value: filterParams.productState === "published",
        },
      }),
    );
  }
  return filters;
}

export const searchContentLibrary = async ({
  filterParams,
  sortOption,
  paginationOption,
}: SearchContentLibraryParams): Promise<SearchProductsResponse> => {
  const req = new SearchProductsRequest();
  req.filters = convertFiltersToGrpc(filterParams);

  //TODO LIB-2744 remove this flag when BE no longer requires it
  req.useLocalDb = true;

  req.sort = new Sort({
    field:
      sortOption.option === "downloadOrder"
        ? Sort_Field.DOWNLOAD_COUNTS
        : Sort_Field.CREATED_AT,
    order: sortOption.order === "asc" ? SortDirection.ASC : SortDirection.DESC,
  });
  req.pagination = new Pagination({
    //  offset is a zero based index
    page: paginationOption.offset + 1,
    pageSize: paginationOption.pageSize,
  });

  return supportServiceConnectClient.searchProducts(req);
};

export const downloadTemplate = async () => {
  const req = new DownloadFileTemplateRequest();
  return supportServiceConnectClient.downloadFileTemplate(req);
};

export const bulkImportContent = async (file: File, regionCode: RegionCode) => {
  const email = await getScUserEmail();
  const arrayBuffer = await file.arrayBuffer();
  const chunk = new Uint8Array(arrayBuffer);

  const headers = callOptionsHeaders(regionCode, file.name);

  return supportServiceConnectClient.bulkImportContents(
    new BulkImportProductsRequest({
      chunk,
      requesterId: email,
    }),
    {
      headers,
    },
  );
};

export const exportProducts = async (
  filters: FilterParams,
  productIds?: string[],
) => {
  const req = new ExportProductsRequest();

  if (productIds && productIds.length > 0) {
    req.productIds = productIds;
  } else {
    req.filters = convertFiltersToGrpc(filters);
  }
  return supportServiceConnectClient.exportProducts(req);
};
